import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import api from "../lib/api";
import {
  Gauge, Users, Receipt, Package, ArrowsLeftRight, Wallet, SignOut,
  List, Lightning, FileText, Headset, UserCircle, Crown, BugBeetle, CloudArrowUp,
  ArrowSquareOut, Factory, Tag, Bell, CheckCircle, Warning, ArrowsClockwise, UserPlus,
  X, House, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const MYZE_URL = "https://app.myze.brother.digital/login?next=/dashboard";
const TF_BLUE = "#0099FF";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [counters, setCounters] = useState({ activations: 0, recharges: 0 });

  // Chiudi drawer mobile su cambio route
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const loadNotifs = useCallback(async () => {
    if (!user) return;
    try {
      if (user.role === "admin") {
        const [{ data: n }, { data: s }] = await Promise.all([
          api.get("/admin/notifications"),
          api.get("/admin/stats"),
        ]);
        // Filter out dismissed admin notifs (client-side only, localStorage)
        let dismissed = [];
        try { dismissed = JSON.parse(localStorage.getItem("tf_dismissed_admin_notifs") || "[]"); } catch (e) { dismissed = []; }
        const filtered = (n || []).filter((x) => !dismissed.includes(x.id));
        setNotifs(filtered);
        setCounters({
          activations: s?.pending_activations || 0,
          recharges: s?.pending_recharges || 0,
        });
      } else {
        const { data } = await api.get("/client/notifications");
        setNotifs(data || []);
        setCounters({ activations: 0, recharges: 0 });
      }
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 60_000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const adminNav = [
    { to: "/admin", label: t("dashboard"), icon: Gauge, end: true },
    { to: "/admin/pending-activations", label: "Attivazioni", icon: UserPlus, badge: "activations" },
    { to: "/admin/clients", label: t("clients"), icon: Users },
    { to: "/admin/pricelists", label: t("pricelists"), icon: Receipt },
    { to: "/admin/orders", label: t("orders"), icon: Package },
    { to: "/admin/transactions", label: t("transactions"), icon: ArrowsLeftRight },
    { to: "/admin/recharges", label: t("recharges"), icon: Wallet, badge: "recharges" },
    { to: "/admin/tickets", label: t("support"), icon: Headset },
    { to: "/admin/simulate", label: t("simulate_order"), icon: Lightning },
    { to: "/admin/branding", label: "Branding & Packaging", icon: Sparkle },
    { to: "/admin/myze-import", label: "Importa ordini", icon: CloudArrowUp },
    { to: "/admin/myze-log", label: "Myze Log", icon: BugBeetle },
  ];
  const clientNav = [
    { to: "/client", label: t("dashboard"), icon: Gauge, end: true },
    { to: "/client/recharge", label: t("recharge"), icon: Wallet },
    { to: "/client/orders", label: t("orders"), icon: Package },
    { to: "/client/pricelist", label: t("nav_pricelist"), icon: Tag },
    { to: "/client/branding", label: t("nav_branding"), icon: Sparkle },
    { to: "/client/transactions", label: t("transactions"), icon: ArrowsLeftRight },
    { to: "/client/invoices", label: t("invoices"), icon: FileText },
    { to: "/client/subscription", label: t("nav_subscription"), icon: Crown },
    { to: "/client/support", label: t("support"), icon: Headset },
    { to: "/client/profile", label: t("profile"), icon: UserCircle },
  ];
  const nav = user?.role === "admin" ? adminNav : clientNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-[var(--tf-bg)] overflow-x-hidden">
      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="mobile-drawer-backdrop"
        />
      )}

      {/* Sidebar (desktop static + mobile off-canvas drawer) */}
      <aside
        className={`${collapsed ? "md:w-16" : "md:w-64"} w-72 flex-shrink-0 bg-[var(--tf-dark)] text-white flex flex-col transition-all duration-200 fixed md:relative inset-y-0 left-0 z-50 md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        {/* Close button (mobile only) */}
        <button
          onClick={() => setMobileOpen(false)}
          className="md:hidden absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
          aria-label={t("close_menu_aria")}
          data-testid="close-mobile-drawer"
        >
          <X size={18} weight="bold" />
        </button>
        <Link
          to={user?.role === "admin" ? "/admin" : "/client"}
          className="h-16 flex items-center px-4 gap-3 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[var(--tf-primary)]/40 rounded-lg"
          data-testid="sidebar-logo-link"
          aria-label="Vai alla dashboard"
        >
          <div className="w-9 h-9 bg-[var(--tf-primary)] rounded-xl flex items-center justify-center flex-shrink-0">
            <img src="/teefarm-logo.png" alt="Teefarm" className="w-6 h-6 object-contain" />
          </div>
          {!collapsed && (
            <div>
              <div className="font-display font-black text-base leading-none tracking-tight text-white">Teefarm</div>
              <div className="tf-label mt-1" style={{ color: "var(--tf-dark-muted)" }}>Connect</div>
            </div>
          )}
        </Link>
        <nav className="flex-1 py-3 scroll-thin overflow-y-auto">
          {nav.map((item) => {
            const count = item.badge ? (counters[item.badge] || 0) : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `tf-nav-item ${isActive ? "active" : ""} ${collapsed ? "!mx-auto !my-1.5 !w-11 !h-11 !p-0 !justify-center" : ""}`}
                data-testid={`nav-${item.to.split("/").pop() || "home"}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={collapsed ? 25 : 18} weight="bold" />
                {!collapsed && (
                  <span className="flex items-center justify-between flex-1 gap-2">
                    <span>{item.label}</span>
                    {count > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--tf-primary)] text-[var(--tf-primary-text)] text-[10px] font-black tf-numeric">
                        {count}
                      </span>
                    )}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* Configurator section - external link to Teefarm Connect */}
          <div className="mt-6 pt-4 mx-3 border-t border-white/10">
            <a
              href={MYZE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`tf-nav-item group !mx-0 ${collapsed ? "!mx-auto !my-1.5 !w-11 !h-11 !p-0 !justify-center" : ""}`}
              data-testid="nav-myze-external"
              title={t("configurator_tooltip")}
            >
              <Factory size={collapsed ? 25 : 18} weight="bold" />
              {!collapsed && (
                <span className="flex items-center justify-between flex-1 gap-2">
                  <span>{t("configurator_full")}</span>
                  <ArrowSquareOut size={13} weight="bold" className="opacity-50 group-hover:opacity-100" />
                </span>
              )}
            </a>
            {!collapsed && (
              <div className="px-4 mt-1.5 text-[10px] leading-snug text-white/40">
                {t("configurator_desc")}
              </div>
            )}
          </div>
        </nav>
        <div className="border-t border-white/10 p-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} text-xs uppercase tracking-widest text-white/50 hover:text-white px-2 py-2 rounded-lg hover:bg-white/5 transition-colors`}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Espandi menu" : undefined}
          >
            <List size={collapsed ? 22 : 16} weight="bold" />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        <header className="h-16 border-b border-[var(--tf-border)] bg-white flex items-center justify-between px-4 md:px-6 sticky top-0 z-30 gap-3 md:gap-4">
          {/* Hamburger mobile */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-10 h-10 rounded-xl flex items-center justify-center text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition"
            aria-label="Apri menu"
            data-testid="mobile-menu-toggle"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex flex-col justify-center leading-tight min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold whitespace-nowrap overflow-hidden">
              <span className="shrink-0">{user?.role === "admin" ? "ADMIN CONSOLE" : "CLIENT PORTAL"}</span>
              <span className="opacity-30 shrink-0">·</span>
              <span className="hidden lg:inline-flex items-center gap-1 normal-case tracking-normal font-normal text-[10px] min-w-0 truncate">
                <span className="font-display font-bold text-[var(--tf-ink)]">Teefarm Connect</span>
                <span className="opacity-40">+</span>
                <span>Myze</span>
              </span>
            </div>
            <div className="font-display font-bold text-sm mt-0.5 truncate">
              {user?.name} <span className="text-[var(--tf-muted)] font-normal">· {user?.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher persistEndpoint={user?.role === "admin" ? "/admin/profile/language" : "/client/profile/language"} />
            <NotifBell notifs={notifs} open={notifOpen} setOpen={setNotifOpen} navigate={navigate} onRead={loadNotifs} isAdmin={user?.role === "admin"} />
            <a
              href={MYZE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tf-btn bg-white text-sm font-display font-bold transition-all hover:shadow-[0_4px_12px_rgba(0,153,255,0.18)]"
              style={{ borderWidth: 3, borderStyle: "solid", borderColor: TF_BLUE, color: TF_BLUE, lineHeight: 1 }}
              data-testid="header-myze-link"
              title={t("configurator_tooltip")}
            >
              <Factory size={16} weight="bold" className="block shrink-0" />
              <span className="leading-none">{t("configurator_short")}</span>
              <ArrowSquareOut size={12} weight="bold" className="block shrink-0 opacity-70" />
            </a>
            <button onClick={handleLogout} className="tf-btn tf-btn-ghost" data-testid="logout-btn">
              <SignOut size={16} weight="bold" />
              {t("logout")}
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 md:pb-6 overflow-auto">{children}</main>

        {/* Bottom nav bar (mobile only) */}
        {user?.role === "client" && (
          <nav
            className="md:hidden fixed bottom-0 inset-x-0 z-[60] bg-white border-t border-[var(--tf-border)] px-2 py-2 flex items-end justify-around"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
            data-testid="mobile-bottom-nav"
          >
            <BottomTab to="/client" icon={House} label={t("dashboard")} testId="bottom-home" />
            <BottomTab to="/client/orders" icon={Package} label={t("orders")} testId="bottom-orders" />
            <NavLink
              to="/client/recharge"
              className="flex flex-col items-center -mt-6"
              data-testid="bottom-recharge"
              aria-label={t("feed_wallet_recharge")}
            >
              <span className="w-14 h-14 rounded-full bg-[var(--tf-primary)] flex items-center justify-center shadow-lg border-4 border-white">
                <Lightning size={26} weight="fill" className="text-[var(--tf-ink)]" />
              </span>
              <span className="text-[10px] font-black tracking-widest text-[var(--tf-ink)] mt-1 uppercase">{t("recharge")}</span>
            </NavLink>
            <BottomTab to="/client/invoices" icon={FileText} label={t("invoices")} testId="bottom-invoices" />
            <BottomTab to="/client/profile" icon={UserCircle} label={t("profile")} testId="bottom-profile" />
          </nav>
        )}
        {/* Bottom nav bar admin (mobile only) */}
        {user?.role === "admin" && (
          <nav
            className="md:hidden fixed bottom-0 inset-x-0 z-[60] bg-white border-t border-[var(--tf-border)] px-2 py-2 flex items-end justify-around"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
            data-testid="admin-bottom-nav"
          >
            <BottomTab to="/admin" icon={Gauge} label="Home" testId="admin-bottom-home" />
            <BottomTab to="/admin/clients" icon={Users} label="Clienti" testId="admin-bottom-clients" />
            <NavLink
              to="/admin/pending-activations"
              className="flex flex-col items-center -mt-6"
              data-testid="admin-bottom-activations"
              aria-label="Attivazioni"
            >
              <span className="w-14 h-14 rounded-full bg-[var(--tf-primary)] flex items-center justify-center shadow-lg border-4 border-white relative">
                <UserPlus size={26} weight="fill" className="text-[var(--tf-ink)]" />
                {counters.activations > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-[var(--tf-error)] text-white text-[10px] font-black tf-numeric flex items-center justify-center px-1 border-2 border-white">
                    {counters.activations > 9 ? "9+" : counters.activations}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-black tracking-widest text-[var(--tf-ink)] mt-1 uppercase">Attivazioni</span>
            </NavLink>
            <BottomTab to="/admin/orders" icon={Package} label="Ordini" testId="admin-bottom-orders" />
            <BottomTab to="/admin/recharges" icon={Wallet} label="Ricariche" testId="admin-bottom-recharges" />
          </nav>
        )}
        <footer className="border-t border-[var(--tf-border)] bg-white px-6 py-3 text-[11px] text-[var(--tf-muted)] flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--tf-primary)]" />
            <span><b className="text-[var(--tf-ink)]">Teefarm Connect</b> <span className="opacity-60">powered by Teefarm S.A.S.</span></span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <NavLink to="/legal/terms" className="hover:text-[var(--tf-ink)]" data-testid="footer-terms">{t("legal_terms_short")}</NavLink>
            <span className="opacity-30">·</span>
            <NavLink to="/legal/privacy" className="hover:text-[var(--tf-ink)]" data-testid="footer-privacy">{t("legal_privacy_short")}</NavLink>
            <span className="opacity-30">·</span>
            <NavLink to="/legal/cookies" className="hover:text-[var(--tf-ink)]" data-testid="footer-cookies">{t("legal_cookies_short")}</NavLink>
            <span className="opacity-30">·</span>
            <a href={MYZE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--tf-ink)] inline-flex items-center gap-1" data-testid="footer-myze">
              {t("configurator_short")} <ArrowSquareOut size={11} weight="bold" />
            </a>
            <span className="opacity-30">·</span>
            <span>© Teefarm S.A.S. · P.IVA 05217980282</span>
          </div>
        </footer>
      </div>
    </div>
  );
}


/* ---------------- Notification Bell ---------------- */
function NotifBell({ notifs, open, setOpen, navigate, onRead, isAdmin }) {
  const { t, lang } = useI18n();
  const locale = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" }[lang] || "it-IT";
  const unread = notifs.length;
  const iconFor = (n) => {
    // Admin uses `type`, client uses `level`
    const key = n.type || n.level || "default";
    switch (key) {
      case "pending_activation": return { Icon: UserPlus, chip: "tf-chip-lime" };
      case "pending_recharge":   return { Icon: ArrowsClockwise, chip: "tf-chip-peach" };
      case "blocked_order":
      case "critical":           return { Icon: Warning, chip: "tf-chip-rose" };
      case "recharge_approved":  return { Icon: CheckCircle, chip: "tf-chip-mint" };
      case "success":            return { Icon: CheckCircle, chip: "tf-chip-mint" };
      case "low_balance":
      case "warning":            return { Icon: Wallet, chip: "tf-chip-peach" };
      case "info":               return { Icon: Bell, chip: "tf-chip-sky" };
      default:                   return { Icon: Bell, chip: "tf-chip-cream" };
    }
  };
  const titleOf = (n) => {
    if (n.title) return n.title;
    if (n.level === "critical") return t("notif_blocked_order");
    if (n.level === "success") return t("notif_ready");
    if (n.level === "warning") return t("notif_attention");
    if (n.level === "info") return t("notif_default");
    return t("notif_default");
  };
  const bodyOf = (n) => n.body || n.message || "";

  const clearAll = async () => {
    if (unread === 0) return;
    if (isAdmin) {
      // Client-side dismiss (admin notifications are aggregated on-the-fly)
      try {
        let dismissed = [];
        try { dismissed = JSON.parse(localStorage.getItem("tf_dismissed_admin_notifs") || "[]"); } catch (e) { dismissed = []; }
        const ids = notifs.map((n) => n.id);
        const merged = Array.from(new Set([...dismissed, ...ids]));
        localStorage.setItem("tf_dismissed_admin_notifs", JSON.stringify(merged));
        toast.success(t("notif_mark_all_read"));
        onRead?.();
      } catch (e) {
        toast.error(t("error"));
      }
      return;
    }
    try {
      await api.post("/client/notifications/clear");
      toast.success(t("notif_mark_all_read"));
      onRead?.();
    } catch (e) {
      toast.error(t("error"));
    }
  };

  const deleteOne = async (e, id) => {
    e.stopPropagation();
    if (isAdmin) {
      // Client-side dismiss (localStorage)
      try {
        let dismissed = [];
        try { dismissed = JSON.parse(localStorage.getItem("tf_dismissed_admin_notifs") || "[]"); } catch (er) { dismissed = []; }
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem("tf_dismissed_admin_notifs", JSON.stringify(dismissed));
        onRead?.();
      } catch (err) {
        toast.error(t("error"));
      }
      return;
    }
    try {
      await api.delete(`/client/notifications/${id}`);
      onRead?.();
    } catch (err) {
      toast.error(t("error"));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-10 h-10 rounded-2xl flex items-center justify-center text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition relative"
        data-testid="notif-bell"
        title={t("notif_title")}
      >
        <Bell size={18} weight="fill" />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] rounded-full bg-[var(--tf-error)] text-white text-[9px] font-black tf-numeric flex items-center justify-center leading-none border border-white pointer-events-none"
            style={{ padding: "0 3px" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Full-screen backdrop dismisses on click */}
          <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          {/* Panel: fixed on mobile (top-right), absolute anchored below bell on desktop */}
          <div
            className="fixed md:absolute right-3 md:right-0 top-16 md:top-full md:mt-2 w-[calc(100vw-1.5rem)] max-w-[380px] md:w-[380px] max-h-[400px] md:max-h-[500px] bg-white z-[90] flex flex-col shadow-2xl border border-[var(--tf-border)]"
            data-testid="notif-panel"
            style={{ borderRadius: "16px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[var(--tf-border)] flex items-center justify-between gap-2 bg-white" style={{ borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}>
              <div className="min-w-0">
                <div className="font-display font-bold text-base text-[var(--tf-ink)]">{t("notif_title")}</div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] mt-0.5">
                  {unread === 0 ? t("notif_all_caught_up") : t("notif_to_review", { n: unread })}
                </div>
              </div>
              {unread > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs font-semibold text-[var(--tf-muted)] hover:text-[var(--tf-ink)] hover:underline transition flex-shrink-0"
                  data-testid="notif-clear-all"
                >
                  {t("notif_mark_all_read")}
                </button>
              )}
            </div>
            <div className="overflow-y-auto scroll-thin flex-1">
              {notifs.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#D1F0E0] text-[#1F6E4A] flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={24} weight="fill" />
                  </div>
                  <div className="font-display font-bold text-sm text-[var(--tf-ink)]">{t("notif_empty_title")}</div>
                  <div className="text-xs text-[var(--tf-muted)] mt-1">{t("notif_empty_body")}</div>
                </div>
              ) : notifs.map((n) => {
                const { Icon, chip } = iconFor(n);
                const ts = n.created_at ? new Date(n.created_at).toLocaleString(locale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                return (
                  <div
                    key={n.id}
                    className="w-full border-b border-[var(--tf-border)] last:border-b-0 hover:bg-[var(--tf-bg)]/40 flex items-start gap-2 transition-colors group"
                    data-testid={`notif-${n.id}`}
                  >
                    <button
                      onClick={() => { if (n.action_url) navigate(n.action_url); setOpen(false); }}
                      className="flex-1 text-left px-4 py-3 flex items-start gap-3 min-w-0"
                    >
                      <div className={`tf-chip ${chip} !w-9 !h-9 !rounded-xl shrink-0`}>
                        <Icon size={16} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[var(--tf-ink)] truncate">{titleOf(n)}</div>
                        <div className="text-xs text-[var(--tf-muted)] mt-0.5 line-clamp-2">{bodyOf(n)}</div>
                        {ts && <div className="text-[10px] text-[var(--tf-muted)] mt-1 tf-numeric">{ts}</div>}
                      </div>
                    </button>
                    {(
                      <button
                        onClick={(e) => deleteOne(e, n.id)}
                        className="mt-3 mr-3 w-7 h-7 rounded-lg flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-error)] hover:bg-[var(--tf-bg)] transition-all md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 flex-shrink-0"
                        title={t("notif_remove")}
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <X size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BottomTab({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `flex flex-col items-center gap-0.5 flex-1 min-w-0 py-1 transition-colors ${isActive ? "text-[var(--tf-ink)]" : "text-[var(--tf-muted)]"}`
      }
      data-testid={testId}
    >
      <Icon size={22} weight="bold" />
      <span className="text-[10px] font-semibold">{label}</span>
    </NavLink>
  );
}
